import { FC, useMemo, SyntheticEvent } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

export interface IOption {
  label: string;
  value: string;
}

interface IOptionInternal extends IOption {
  disabled?: boolean;
}

interface InfinitySelectProps {
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  getDisabledOptions?: string[] | (() => string[]);
  options?: IOption[];
  value?: IOption | null;
  inputValue?: string;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChange?: (value: IOption | null) => void;
  label?: string;
  listboxRef?: any;
  sx?: object;
  placeholder?: string;
}

export const InfinitySelect: FC<InfinitySelectProps> = ({
  options = [],
  value,
  inputValue,
  onChange,
  onInputChange,
  loadMore,
  hasMore,
  loading,
  label,
  listboxRef,
  sx,
  placeholder
}) => {
  const parsedOptions: IOptionInternal[] = useMemo(
    () =>
      (options ?? []).map((o) => ({
        ...o,
        label: o.label?.replace(/\s+/g, ' ').trim() ?? '',
      })),
    [options]
  );

  const handleScroll = (event: SyntheticEvent) => {
    const target = event.currentTarget as HTMLDivElement;
    const scrollThreshold = 50;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - scrollThreshold) {
      if (!loading && hasMore) loadMore();
    }
  };

  return (
    <Autocomplete<IOptionInternal>
      value={value as IOptionInternal | null}
      inputValue={inputValue ?? ''}
      options={parsedOptions}
      getOptionLabel={(option) => option.label}
      filterOptions={(options) => options}
      onChange={(_, newValue) => onChange && onChange(newValue)}
      onInputChange={(_, newInputValue) => {
        if (onInputChange) {
          const fakeEvent = { target: { value: newInputValue } } as React.ChangeEvent<HTMLInputElement>;
          onInputChange(fakeEvent);
        }
      }}
      sx={{
        "& fieldset": {
          borderRadius: "12px",
          border: "2px solid #C0D4E3",
        },
        ...sx,
      }}
      ListboxProps={{
        onScroll: handleScroll,
        ref: listboxRef,
        style: { maxHeight: 350 },
      }}
      renderOption={(props, option) => <li {...props}>{option.label}</li>}
      renderTags={() => null}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>{params.InputProps.endAdornment}</>,
            sx: {
              "& input::placeholder": {
                fontStyle: "italic",
                color: "rgba(0,0,0,0.6)"
              }
            }
          }}
        />
      )}
      renderGroup={(params) => (
        <>
          {params.children}
          {loading && (
            <li style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
              <CircularProgress size={20} />
            </li>
          )}
        </>
      )}
    />
  );
};
